import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  ViewChild
} from '@angular/core';
import {Subscription} from 'rxjs';
import {ReactedWithEnum} from "../../../core/enums/reacted-with.enum";
import {ReactionTypeEnum} from "../../../core/enums/reaction-type.enum";
import {ButtonDirective} from "primeng/button";
import {DividerModule} from "primeng/divider";
import {EmojiComponent} from "@ctrl/ngx-emoji-mart/ngx-emoji";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {NgxLongPress2Module} from "ngx-long-press2";
import {PipesModule} from "../../../core/pipes/pipes.module";
import {SpeedDialModule} from "../../speed-dial/speed-dial.module";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {EndCapModule} from "../../end-cap/end-cap.module";
import {NgxPermissionsModule} from "ngx-permissions";
import {SkeletonModule} from "primeng/skeleton";
import {SocialModule} from "../social.module";
import {ThumbnailModule} from "../../thumbnail/thumbnail.module";
import {ReactionApiModel} from "../../../api/models/reaction.api.model";
import {UserApiModel} from "../../../api/models/user.api-model";
import {PostViewModel} from "../../timeline/post.view-model";
import {ReactionSubjectInterface} from "../../timeline/timeline-tile/reaction-subject.interface";
import {CurrentUserViewModel} from "../../../core/models/current-user.view-model";
import {ReactedWithToEmojiMapper} from "../../../core/helpers/reacted-with-to-emoji.mapper";
import {AbstractComponent} from "../../../core/components/abstract-component";
import {MenuItem} from "primeng/api";
import {TimelineDataProviderService} from "../../timeline/timeline.data-provider.service";
import {SpeedDialComponent} from "../../speed-dial/speed-dial.component";
import {ResultListApiModel} from "../../../api/models/result-list.api.model";
import {CommentApiModel} from "../../../api/models/comment.api.model";
import {KeyValuePairInterface} from "../../interfaces/key-value-pair.interface";
import {SocialInputSubmitInterface} from "../social-input/social-input-submit.interface";
import {TruncateOnWordPipe} from "../../../core/pipes/truncate-on-word.pipe";
import {PostTypeEnum} from "../../../core/enums/post-type.enum";
import {Router} from "@angular/router";
import {ReactionActionTypes} from "../../../state/reaction/reaction.actions";
import {ModalService} from "../../modal/modal.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../../state/app.state";
import {QrDialogComponent, QrDialogData} from "../../qr/qr-dialog.component";
import {PublicResourceUrlService} from "../../services/public-resource-url.service";
import {ReactionListCacheFacade} from '../../modal/components/social-list/reaction-list-cache.facade';
import {MenuModule} from 'primeng/menu';
import {ProjectViewModel} from '../../../features/user/garage/project.view-model';
import {EventApiModel} from '../../../api/models/event.api.model';
import {CommentMediaComponent} from '../comment-media/comment-media.component';

@Component({
  selector: 'app-social-actions',
  standalone: true,
  imports: [
    ButtonDirective,
    DividerModule,
    EmojiComponent,
    NgIf,
    NgxLongPress2Module,
    PipesModule,
    SpeedDialModule,
    TranslateModule,
    EndCapModule,
    NgForOf,
    NgxPermissionsModule,
    SkeletonModule,
    SocialModule,
    ThumbnailModule,
    NgClass,
    QrDialogComponent,
    MenuModule,
    CommentMediaComponent
  ],
  templateUrl: './social-actions.component.html',
  styleUrl: './social-actions.component.scss'
})
export class SocialActionsComponent extends AbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  get preloadedComments(): boolean {
    return this._preloadedComments;
  }

  @Input() set preloadedComments(value: boolean) {
    this._preloadedComments = value;
    this.ensureCommentBoxOpened();
  }
  get post(): PostViewModel {
    return this._post;
  }

  @Input() set post(value: PostViewModel) {
    const previousPostId = this._post?.id;
    const previousCommentsCount = this._post?.commentsCount || 0;
    this._post = PostViewModel.ensureInstance(value);
    const postChanged = previousPostId !== undefined && previousPostId !== this._post?.id;
    const commentsCountChanged = previousPostId === this._post?.id
      && previousCommentsCount !== (this._post?.commentsCount || 0);
    if (postChanged || commentsCountChanged) {
      this.resetCommentsForPost();
    }
    if ((this._post.reactions?.length || this._post.currentUserReaction) && this.currentUser) {
      this.reactionSubject = this._post;
      this.syncIsReacted();
    }
    this.ensureCommentBoxOpened();
  }

  @Input() currentUser: CurrentUserViewModel;
  @Input() socialModalDisabled: boolean = false;
  @Input() shareTargetProject: ProjectViewModel | null = null;
  @Input() shareTargetEvent: EventApiModel | null = null;
  @Output() commentContextMenuOpen: EventEmitter<CommentApiModel> = new EventEmitter<CommentApiModel>();
  @Output() threadListUnfolded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() sharedPostCreated: EventEmitter<PostViewModel> = new EventEmitter<PostViewModel>();
  @Output() shareCountIncremented: EventEmitter<{ postId: number; delta: number }> = new EventEmitter<{ postId: number; delta: number }>();

  public readonly ReactedWithEnum = ReactedWithEnum;
  public readonly ReactionTypeEnum = ReactionTypeEnum;
  public items: MenuItem[];
  public shareMenuItems: MenuItem[] = [];
  public reactionSubject: ReactionSubjectInterface;
  public reactionType: ReactionTypeEnum;
  public isCommentBoxOpened: boolean = false;
  public isThreadListOpened = false;
  public commentBoxLoaded = false;
  public comments: CommentApiModel[] = [];
  public isThreadCommentListOpened: KeyValuePairInterface<number, boolean>[] = [];
  public isThreadCommentInputOpened: KeyValuePairInterface<number, boolean>[] = [];
  public mobileReplyContextComment: CommentApiModel | null = null;
  public desktopReplyContextComment: CommentApiModel | null = null;
  public desktopReplyThreadId: number | null = null;
  public qrDialogVisible = false;
  public qrDialogData: QrDialogData | null = null;
  private commentsLayoutEl: HTMLElement | null = null;
  private stickyInputEl: HTMLElement | null = null;
  private stickyResizeObserver: ResizeObserver | null = null;
  private _post: PostViewModel;
  private _preloadedComments: boolean = false;
  /** A gallery component is reused while slides change; never let an older request update a newer post. */
  private commentLoadSubscription: Subscription | null = null;
  private commentLoadVersion = 0;

  @ViewChild('commentsLayout')
  set commentsLayoutRef(ref: ElementRef<HTMLElement> | undefined) {
    this.commentsLayoutEl = ref?.nativeElement ?? null;
    this.bindStickyInputHeightObserver();
  }

  @ViewChild('stickyInput')
  set stickyInputRef(ref: ElementRef<HTMLElement> | undefined) {
    this.stickyInputEl = ref?.nativeElement ?? null;
    this.bindStickyInputHeightObserver();
  }
  @ViewChild('shareMenu') shareMenu: any;
  constructor(
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly ms: ModalService,
    private readonly store: Store<AppState>,
    private readonly publicResourceUrlService: PublicResourceUrlService,
    private readonly reactionListCacheFacade: ReactionListCacheFacade,
  ) {
    super();
  }

  ngOnInit() {
    this.items = [
      {
        state: {
          emoji: ReactedWithToEmojiMapper.map(ReactedWithEnum.FIRE)
        },
        command: () => {
          this.subscriptions.push(this.timelineDPS.createReaction(ReactedWithEnum.FIRE, this.reactionSubject, this.reactionType).subscribe((reactionAM: ReactionApiModel) => {
            this.syncReaction(reactionAM);
          }));
        }
      },
      {
        state: {
          emoji: ReactedWithToEmojiMapper.map(ReactedWithEnum.HEART)
        },
        command: () => {
          this.subscriptions.push(this.timelineDPS.createReaction(ReactedWithEnum.HEART, this.reactionSubject, this.reactionType).subscribe((reactionAM: ReactionApiModel) => {
            this.syncReaction(reactionAM);
          }));
        }
      },
      {
        state: {
          emoji: ReactedWithToEmojiMapper.map(ReactedWithEnum.THUMBS_UP)
        },
        command: () => {
          this.subscriptions.push(this.timelineDPS.createReaction(ReactedWithEnum.THUMBS_UP, this.reactionSubject, this.reactionType).subscribe((reactionAM: ReactionApiModel) => {
            this.syncReaction(reactionAM);
          }));
        }
      },
      {
        state: {
          emoji: ReactedWithToEmojiMapper.map(ReactedWithEnum.FACE_SURPRISE)
        },
        command: () => {
          this.subscriptions.push(this.timelineDPS.createReaction(ReactedWithEnum.FACE_SURPRISE, this.reactionSubject, this.reactionType).subscribe((reactionAM: ReactionApiModel) => {
            this.syncReaction(reactionAM);
          }));
        }
      },
    ];

    this.shareMenuItems = [
      {
        label: this.translate.instant('shared.share.menu.feed'),
        icon: 'pi pi-megaphone',
        command: () => this.openShareComposer(),
      },
      {
        label: this.translate.instant('shared.share.menu.chat'),
        icon: 'pi pi-send',
        command: () => this.openChatShareModal(),
      },
      {
        label: this.translate.instant('shared.share.menu.external'),
        icon: 'pi pi-qrcode',
        command: () => this.openQrDialog(),
      }
    ];
  }

  ngAfterViewInit(): void {
    this.bindStickyInputHeightObserver();
  }

  ngOnDestroy() {
    this.commentLoadSubscription?.unsubscribe();
    this.commentLoadSubscription = null;
    this.stickyResizeObserver?.disconnect();
    this.stickyResizeObserver = null;
    super.ngOnDestroy();
  }

  public syncReaction(reactionAM: ReactionApiModel): void {
    const hadCurrentUserReaction = !!this.reactionSubject.currentUserReaction;
    this.reactionSubject.reactions = this.reactionSubject.reactions.filter((x: ReactionApiModel) => (x.author as UserApiModel)['@id'] !== (reactionAM.author as UserApiModel)['@id']);
    this.reactionSubject.reactions.unshift(reactionAM);
    if (!hadCurrentUserReaction) {
      this.reactionSubject.reactionsCount = (this.reactionSubject.reactionsCount || 0) + 1;
    }
    this.syncIsReacted();
    this.reactionListCacheFacade.addReactionToTargets(reactionAM, [
      {listType: 'local-subject-reactions', subjectId: this.reactionSubject['@id']}
    ]);
  }

  public syncIsReacted(): void {
    this.reactionSubject.currentUserReaction = this.reactionSubject.reactions?.find((x: ReactionApiModel) => (x.author as UserApiModel)['@id'] === this.currentUser['@id']) || this.reactionSubject.currentUserReaction || null;
  }

  public toggleReactionBtn(e, subject: ReactionSubjectInterface, type: ReactionTypeEnum, speedDial: SpeedDialComponent) {
    e.preventDefault();
    e.stopPropagation();
    this.reactionType = type;
    this.reactionSubject = subject;
    speedDial.onButtonClick(e);
  }

  onCommentBoxOpen(postVM: PostViewModel): void {
    if (this.socialModalDisabled) {
      return;
    }

    if (!this.isCommentBoxOpened) {
      this.loadCommentBox(postVM);
      this.isCommentBoxOpened = true;
      this.cdr.detectChanges();
      return;
    }

    this.isCommentBoxOpened = false;
    this.cdr.detectChanges();
  }

  private ensureCommentBoxOpened(): void {
    if (!this._preloadedComments || !this._post || this.isCommentBoxOpened) {
      return;
    }

    // `preloadedComments` means that the sheet should be ready before the
    // user taps the comments action.  A post whose API summary already says
    // it has no comments needs no collection request: render its empty state
    // immediately and leave the network free for the active video.
    if (!this._post.commentsCount) {
      this.comments = [];
      this.commentBoxLoaded = true;
      this.isCommentBoxOpened = true;
      this.cdr.detectChanges();
      return;
    }

    this.loadCommentBox(this._post);
    this.isCommentBoxOpened = true;
    this.cdr.detectChanges();
  }

  private loadCommentBox(postVM: PostViewModel): void {
    this.commentLoadSubscription?.unsubscribe();
    const requestVersion = ++this.commentLoadVersion;
    const postId = postVM?.id;
    this.commentBoxLoaded = false;
    this.comments = [];
    this.isThreadCommentListOpened = [];
    this.isThreadCommentInputOpened = [];
    this.commentLoadSubscription = this.timelineDPS.getCommentCollection(postVM)
      .subscribe({
        next: (resultListAM: ResultListApiModel<CommentApiModel>) => {
          if (requestVersion !== this.commentLoadVersion || postId !== this._post?.id) {
            return;
          }
          this.comments = resultListAM.records;
          this.comments.forEach((thread: CommentApiModel) => {
            thread.currentUserReaction = thread.reactions.find((reaction: ReactionApiModel) => reaction.author['@id'] === this.currentUser['@id']);
            thread.responses.forEach((response: CommentApiModel) => response.currentUserReaction = response.reactions.find((reaction: ReactionApiModel) => reaction.author['@id'] === this.currentUser['@id']));
          });
          this.assignReplyBooleanMap();
          this.assignThreadCommentsMap();
          this.commentBoxLoaded = true;
          this.cdr.detectChanges();
          // this.timeline.ig.updateItems();
        },
        error: () => {
          if (requestVersion !== this.commentLoadVersion || postId !== this._post?.id) {
            return;
          }
          // A failed/cancelled collection must resolve to a stable empty
          // state. Leaving `commentBoxLoaded` false made the skeleton spin
          // forever, including for posts whose summary had become stale.
          this.comments = [];
          this.commentBoxLoaded = true;
          this.cdr.detectChanges();
        },
      });
  }

  private resetCommentsForPost(): void {
    this.commentLoadVersion += 1;
    this.commentLoadSubscription?.unsubscribe();
    this.commentLoadSubscription = null;
    this.comments = [];
    this.isThreadCommentListOpened = [];
    this.isThreadCommentInputOpened = [];
    this.commentBoxLoaded = false;
    this.isCommentBoxOpened = false;
    this.mobileReplyContextComment = null;
    this.desktopReplyContextComment = null;
    this.desktopReplyThreadId = null;
  }

  assignThreadCommentsMap(commentAM?: CommentApiModel): void {
    if (commentAM) {
      this.isThreadCommentListOpened.push(Object.assign({key: commentAM.id, value: false}));
      return;
    }

    this.comments.forEach((commentAM: CommentApiModel) => {
      this.isThreadCommentListOpened.push(Object.assign({key: commentAM.id, value: false}));
    });
  }

  assignReplyBooleanMap(commentAM?: CommentApiModel): void {
    if (commentAM) {
      this.isThreadCommentInputOpened.push(Object.assign({key: commentAM.id, value: false}));
      return;
    }

    this.comments.forEach((commentAM: CommentApiModel) => {
      this.isThreadCommentInputOpened.push(Object.assign({key: commentAM.id, value: false}));
    });
  }

  toggleThreadCommentsMap(commentAM: CommentApiModel): void {
    commentAM.responses = commentAM.responses.reverse();
    const index = this.isThreadCommentListOpened.findIndex(e => e.key === commentAM.id);
    this.isThreadCommentListOpened[index].value = !this.isThreadCommentListOpened[index].value;
  }

  toggleReplyBooleanMap(commentAM: CommentApiModel, contextComment: CommentApiModel = commentAM): void {
    if (this.isMobileView()) {
      this.mobileReplyContextComment = this.mobileReplyContextComment?.id === contextComment.id ? null : contextComment;
      return;
    }

    this.toggleThreadCommentsMap(commentAM);
    const openedEntry = this.isThreadCommentInputOpened.find(e => e.key === commentAM.id);
    const isSameContextClicked = openedEntry?.value
      && this.desktopReplyThreadId === commentAM.id
      && this.desktopReplyContextComment?.id === contextComment.id;

    this.comments.forEach((c: CommentApiModel) => {
      const index = this.isThreadCommentInputOpened.findIndex(e => e.key === c.id);
      this.isThreadCommentInputOpened[index].value = !isSameContextClicked && commentAM.id === c.id;
    });

    if (isSameContextClicked) {
      this.desktopReplyContextComment = null;
      this.desktopReplyThreadId = null;
      return;
    }

    this.desktopReplyContextComment = contextComment;
    this.desktopReplyThreadId = commentAM.id;
  }

  onReplyClick(event: Event, commentAM: CommentApiModel, contextComment: CommentApiModel = commentAM): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleReplyBooleanMap(commentAM, contextComment);
  }

  onConversationTap(): void {
    if (this.isMobileView() && this.mobileReplyContextComment) {
      this.mobileReplyContextComment = null;
    }
  }

  isItemOpened(map: KeyValuePairInterface<number, boolean>[], comment: CommentApiModel): boolean {
    return map.find(e => e.key === comment.id).value;
  }

  toggleThreadList(): void {
    this.isThreadListOpened = !this.isThreadListOpened;
    // this.timeline.ig.updateItems();
    this.threadListUnfolded.next(this.isThreadListOpened);
  }

  public toggleThumbsUp(e, subject: ReactionSubjectInterface, type: ReactionTypeEnum, speedDial: SpeedDialComponent) {
    e.preventDefault();
    this.reactionType = type;
    this.reactionSubject = subject;

    if (speedDial.visible) {
      return;
    }

    if (subject.currentUserReaction) {
      const currentReaction = subject.currentUserReaction;
      this.subscriptions.push(this.timelineDPS.removeReaction(subject.currentUserReaction).subscribe(() => {
        this.reactionListCacheFacade.removeReactionFromTargets(currentReaction['@id'], [
          {listType: 'local-subject-reactions', subjectId: subject['@id']}
        ]);
        subject.reactions = subject.reactions.filter((x: ReactionApiModel) => (x.author as UserApiModel)['@id'] !== (currentReaction.author as UserApiModel)['@id']);
        subject.reactionsCount = Math.max(0, (subject.reactionsCount || subject.reactions.length || 0) - 1);
        subject.currentUserReaction = null;
      }));
      return;
    }

    this.subscriptions.push(this.timelineDPS.createReaction(ReactedWithEnum.THUMBS_UP, subject, this.reactionType).subscribe((reactionAM: ReactionApiModel) => {
      this.syncReaction(reactionAM);
    }));
  }

  onCommentSubmit(event: SocialInputSubmitInterface, postVM: PostViewModel): void {
    if (this.isMobileView() && this.mobileReplyContextComment) {
      const threadComment = this.resolveThreadComment(this.mobileReplyContextComment) || this.mobileReplyContextComment;
      this.onReplySubmit(event, postVM, threadComment);
      return;
    }

    this.subscriptions.push(this.timelineDPS.createComment(event.content, postVM, undefined, event.mediaObjectAMs).subscribe({
      next: (commentAM: CommentApiModel) => {
        event.input.reset();
        this.comments.unshift(commentAM);
        this.post.comments.unshift(commentAM['@id']);
        this.post.commentsCount = (this.post.commentsCount || 0) + 1;
        this.assignReplyBooleanMap(commentAM);
        this.assignThreadCommentsMap(commentAM);
      },
      error: () => event.input.restoreAfterFailedSubmit()
    }));
  }

  onReplySubmit(event: SocialInputSubmitInterface, postVM: PostViewModel, commentAM: CommentApiModel): void {
    const threadComment = this.resolveThreadComment(commentAM) || commentAM;
    this.subscriptions.push(this.timelineDPS.createComment(event.content, postVM, threadComment, event.mediaObjectAMs).subscribe({
      next: (replyAM: CommentApiModel) => {
        event.input.reset();
        const targetThread = this.resolveThreadComment(replyAM) || threadComment;
        targetThread.responses = targetThread.responses || [];
        targetThread.responses.push(replyAM);
        this.mobileReplyContextComment = null;
        this.desktopReplyContextComment = null;
        this.desktopReplyThreadId = null;
      },
      error: () => event.input.restoreAfterFailedSubmit()
    }));
  }

  public upsertComment(commentAM: CommentApiModel): void {
    const thread = this.comments.find(comment => comment['@id'] === (commentAM.thread || commentAM['@id']));
    const current = commentAM.thread
      ? thread?.responses?.find(reply => reply['@id'] === commentAM['@id'])
      : thread;
    if (current) {
      current.content = commentAM.content;
      current.media = commentAM.media;
    }
  }

  public openSocialModal(e: Event, subject: ReactionSubjectInterface) {
    if (this.socialModalDisabled) {
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    this.reactionSubject = subject;
    this.subscriptions.push(this.ms.showReactionsModal({
      listType: 'local-subject-reactions',
      subject,
      initialData: this.reactionSubject.reactions
    }).successState.subscribe());
  }

  public emitCommentContextMenuOpen(commentAM: CommentApiModel): void {
    this.commentContextMenuOpen.emit(commentAM);
  }

  public openQrDialog(): void {
    const sharePost = this.resolveCurrentSharePost();
    this.qrDialogData = {
      resourceType: 'post',
      title: this.getShareTitle(sharePost),
      subtitle: TruncateOnWordPipe.truncateOnWord(sharePost.content, 255, '...'),
      url: this.getPostUrl(sharePost),
    };
    this.qrDialogVisible = true;
  }

  public openShareMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.shareMenu?.toggle(event);
  }

  public openShareComposer(): void {
    if (!this.currentUser) {
      return;
    }

    const sharePost = this.resolveSharePayloadPost();
    this.subscriptions.push(this.ms.showPostComposerModal({
      currentUserVM: this.currentUser,
      projectVM: this.shareTargetProject,
      eventAM: this.shareTargetEvent,
      sharedPost: sharePost,
    }).successState.subscribe((modalStateEvent) => {
      const postVM = modalStateEvent?.data as PostViewModel;
      if (!postVM) {
        return;
      }

      this.emitShareCountIncrement(sharePost.id, 1);
      this.sharedPostCreated.emit(postVM);
    }));
  }

  public openChatShareModal(): void {
    if (!this.currentUser) {
      return;
    }

    const sharePost = this.resolveSharePayloadPost();
    this.subscriptions.push(this.ms.showChatShareModal({
      currentUserVM: this.currentUser,
      sharedPost: sharePost,
    }).successState.subscribe((modalStateEvent) => {
      const sharedMessages = Array.isArray(modalStateEvent?.data) ? modalStateEvent.data : [];
      this.emitShareCountIncrement(sharePost.id, Math.max(1, sharedMessages.length));
    }));
  }

  getPostUrl(post: PostViewModel = this.post): string {
    return this.publicResourceUrlService.getPostUrl(post.id);
  }

  private getShareTitle(post: PostViewModel = this.post): string {
    if (post.type === PostTypeEnum.PROJECT_POST || post.type === PostTypeEnum.PROJECT_MEDIA_POST) {
      return post.project?.vehicle?.fullVehicleIdentity || post.postedBy?.username || 'Post';
    }

    if (post.type === PostTypeEnum.EVENT_DISCUSSION_POST) {
      return post.event?.name || post.postedBy?.username || 'Post';
    }

    if (post.type === PostTypeEnum.COLLABORATION_MEDIA_POST) {
      return post.collaboration?.title
        || post.collaboration?.project?.vehicle?.fullVehicleIdentity
        || post.postedBy?.username
        || 'Post';
    }

    return post.postedBy?.username || 'Post';
  }

  private resolveCurrentSharePost(): PostViewModel {
    return PostViewModel.ensureInstance(this.post);
  }

  private resolveSharePayloadPost(): PostViewModel {
    const post = PostViewModel.ensureInstance(this.post);
    return post?.getShareSourcePost() || post;
  }

  private emitShareCountIncrement(postId: number | undefined, delta: number): void {
    if (!postId || delta <= 0) {
      return;
    }

    this.shareCountIncremented.emit({ postId, delta });
  }

  public getBottomInputLabel(): string {
    if (this.isMobileView() && this.mobileReplyContextComment?.author?.username) {
      return `${this.translate.instant('common.reply')} @${this.mobileReplyContextComment.author.username}`;
    }
    return this.translate.instant('common.write_something');
  }

  public getReplyInputLabel(comment: CommentApiModel): string {
    const username = this.desktopReplyContextComment?.author?.username || comment?.author?.username;
    if (username) {
      return `${this.translate.instant('common.reply')} @${username}`;
    }
    return this.translate.instant('common.write_something');
  }

  public isMobileView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }

  private bindStickyInputHeightObserver(): void {
    if (!this.commentsLayoutEl || !this.stickyInputEl) {
      return;
    }

    const updateStickyHeight = (): void => {
      if (!this.commentsLayoutEl || !this.stickyInputEl) {
        return;
      }

      const stickyHeight = Math.ceil(this.stickyInputEl.getBoundingClientRect().height || 0);
      this.commentsLayoutEl.style.setProperty('--social-input-sticky-height', `${Math.max(0, stickyHeight)}px`);
    };

    this.stickyResizeObserver?.disconnect();
    this.stickyResizeObserver = null;

    updateStickyHeight();

    if (typeof ResizeObserver === 'undefined') {
      requestAnimationFrame(updateStickyHeight);
      return;
    }

    this.stickyResizeObserver = new ResizeObserver(() => updateStickyHeight());
    this.stickyResizeObserver.observe(this.stickyInputEl);
  }

  private resolveThreadComment(commentAM: CommentApiModel | null): CommentApiModel | null {
    if (!commentAM) {
      return null;
    }

    const directThread = this.comments.find((comment: CommentApiModel) => this.isSameComment(comment, commentAM));
    if (directThread) {
      return directThread;
    }

    const parentThread = this.comments.find((comment: CommentApiModel) =>
      (comment.responses || []).some((response: CommentApiModel) => this.isSameComment(response, commentAM))
    );
    if (parentThread) {
      return parentThread;
    }

    if (commentAM.thread) {
      const byThreadRef = this.comments.find((comment: CommentApiModel) => comment['@id'] === commentAM.thread);
      if (byThreadRef) {
        return byThreadRef;
      }
    }

    return null;
  }

  private isSameComment(a: CommentApiModel | null, b: CommentApiModel | null): boolean {
    if (!a || !b) {
      return false;
    }
    return a.id === b.id || (!!a['@id'] && a['@id'] === b['@id']);
  }
}
